import { escapeHtml } from '../../utils/escapeHtml';
import { resolveFulfillmentType } from '../../utils/fulfillmentType';
import { renderBarcodeSvg } from '../../utils/barcode';

export interface DeliveryLabelData {
  orderNumber: string;
  patientFirstName: string;
  patientLastName: string;
  patientPhone?: string; // Patient phone number
  rxNumber: string; // RX number for barcode (first RX in the order)
  rxCount?: number; // Number of RX in this order/delivery
  isRefrigerated?: boolean; // GP-75: Cold chain indicator
  multiBag?: boolean; // GP-75: True when order is part of a cold chain split (2 bags)
  multiBagSibling?: string; // GP-75: Sibling order number (the other bag)
  isNarcotic?: boolean; // GP-454: True when any RX# starts with '9' (narcotic)
  deliveryMethod?: string; // GP-1234: fulfillment type — 'pickup' | 'delivery'
  deliveryType?: string; // GP-333: e.g. InHouse, Courier, 3rd_Party
  shipmentDate?: string; // GP-333: Shipment/delivery date (YYYY-MM-DD)
  /**
   * GP-521/GP-664: Kroll nursing-home group name. Printed on the DELIVERY TAG
   * only (never the vial label — too cramped) so staff can sort packaged orders
   * by home after Product Verification. Absent for non-NH orders.
   */
  nhGroupName?: string;
  deliveryInstructions?: string; // GP-887: second address line (unit notes / delivery notes)
  address: {
    street: string;
    unit?: string; // GP-564: Unit / suite number (prefixed to street on the label)
    city: string;
    province: string;
    postalCode: string;
  };
}

/**
 * Print a delivery label for packaging (GP-375: one label per ORDER)
 * Size: 1-1/8" x 3-1/2" (1.125" x 3.5")
 * Content: Last name, first name, address, and barcode of RX number
 * Printed once at the end of the order, after all RXs are processed.
 * Note: Previously used QR code - commented out for potential reversion
 */
const buildDeliveryLabelMarkup = (data: DeliveryLabelData): string => {
  // GP-564: Prefix unit/suite to the street when present, matching the pattern
  // used by staging-sms.service.ts and the Packaging Queue address column.
  const street = data.address.street || '';
  const unit = data.address.unit || '';
  const streetWithUnit = unit && street ? `${unit} - ${street}` : street;

  // Prepare address lines (escape all user-provided text for HTML safety)
  const addressLine1 = escapeHtml(streetWithUnit);
  const addressLine2 = escapeHtml(`${data.address.city}, ${data.address.province} ${data.address.postalCode}`);

  // GP-1234: the fulfillment type (pickup vs delivery) must be on the label —
  // staff at the bagging/handoff station could not tell a pickup from a courier
  // run without reopening the order. On a pickup the courier routing type is
  // meaningless (and misleading), so PICKUP takes the badge slot outright.
  const fulfillment = resolveFulfillmentType(data.deliveryMethod);
  const routingBadge = fulfillment === 'PICKUP' ? '' : data.deliveryType || '';

  // PRX-39: the barcode is rendered HERE, from the bundled jsbarcode, and
  // inlined into the popup. It used to be drawn by a CDN script inside the
  // popup, and when that script lost the race with window.print() the label
  // silently printed with no barcode.
  const barcodeSvg = renderBarcodeSvg(data.rxNumber, {
    width: 2,
    height: 60,
    displayValue: true,
    fontSize: 10,
    margin: 2,
  });

  // Create label HTML
  const labelHTML = `
    <div class="delivery-label">
      <div class="label-content">
        <!-- Left Section: Text Information -->
        <div class="text-section">
          <!-- Patient Name -->
          <div class="name-section">
            <div class="last-name">${escapeHtml(data.patientLastName.toUpperCase())}</div>
            <div class="first-name">${escapeHtml(data.patientFirstName)}${data.patientPhone ? ` - ${escapeHtml(data.patientPhone)}` : ''}</div>
          </div>

          <!-- Address -->
          <div class="address-section">
            <div class="address-line">${addressLine1}</div>
            <div class="address-line">${addressLine2}</div>
            ${data.deliveryInstructions ? `<div class="instructions-line">${escapeHtml(data.deliveryInstructions)}</div>` : ''}
          </div>

          <!-- Delivery Info -->
          <div class="order-info-row">
            ${data.nhGroupName ? `<span class="nh-group">${escapeHtml(data.nhGroupName)}</span>` : ''}
            ${fulfillment ? `<span class="fulfillment-badge">${fulfillment}</span>` : ''}
            ${routingBadge ? `<span class="delivery-type">${escapeHtml(routingBadge)}</span>` : ''}
            ${data.isNarcotic ? `<span class="sig-required">Sig Required</span>` : ''}
            ${data.shipmentDate ? `<span class="shipment-date">${escapeHtml(data.shipmentDate)}</span>` : ''}
          </div>
          ${data.multiBag ? `<div class="multi-bag">2 BAGS${data.multiBagSibling ? ` + ${escapeHtml(data.multiBagSibling)}` : ''}</div>` : ''}
        </div>

        <!-- Right Section: Barcode (RX Number) -->
        <!-- OLD: QR Code version - commented out for future reversion
        <div class="qr-section">
          <div id="delivery-qr-code"></div>
        </div>
        -->
        <div class="barcode-section">
          ${barcodeSvg}
        </div>
      </div>
    </div>
  `;

  return labelHTML;
};

/**
 * PRX-165 WP-26 — several labels in one print window, one per page. Make
 * shipment prints a courier group's labels together: one window per label
 * would be eaten by the popup blocker after the first.
 */
const MULTI_LABEL_CSS = `
          html, body { height: auto !important; overflow: visible !important; }
          .delivery-label { page-break-after: always; break-after: page; }
          .delivery-label:last-of-type { page-break-after: auto; break-after: auto; }
`;

/** The print window's features, shared by the window Make shipment opens ahead. */
export const DELIVERY_LABEL_WINDOW_FEATURES = 'width=400,height=600';

/**
 * Writes the labels into `target`, or into a window it opens when `target` is
 * not given. Answers false when there is no window to write into (a blocked
 * popup), so a caller never reports a label that did not print.
 */
const writeDeliveryLabelWindow = (
  labelsHtml: string,
  title: string,
  multi: boolean,
  target?: Window | null,
): boolean => {
  const printWindow = target === undefined
    ? window.open('', '_blank', DELIVERY_LABEL_WINDOW_FEATURES)
    : target;
  if (!printWindow) {
    // The packaging and reprint callers say it this way; Make shipment passes
    // its own window and says it in a snackbar.
    if (target === undefined) alert('Please allow pop-ups for this site to print labels');
    return false;
  }

  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <title>${title}</title>
        <style>
          @page {
            size: 3.5in 1.125in;
            margin: 0;
          }

          @media print {
            @page {
              size: 3.5in 1.125in;
              margin: 0;
            }
            html, body {
              width: 3.5in;
              height: 1.125in;
              margin: 0;
              padding: 0;
            }
          }

          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }

          html, body {
            margin: 0;
            padding: 0;
            width: 3.5in;
            height: 1.125in;
            overflow: hidden;
          }

          .delivery-label {
            width: 3.5in;
            height: 1.125in;
            padding: 0.08in;
            background: white;
            font-family: Arial, sans-serif;
            position: relative;
            border: 1px solid #000;
          }

          .label-content {
            width: 100%;
            height: 100%;
            display: flex;
            flex-direction: row;
            gap: 0.08in;
          }

          /* Left Section: Text Information */
          .text-section {
            flex: 1;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
          }

          /* Name Section */
          .name-section {
            border-bottom: 2px solid #000;
            padding-bottom: 0.04in;
            margin-bottom: 0.04in;
          }

          /* GP-887: fonts roughly doubled — drivers couldn't read the label */
          .last-name {
            font-weight: bold;
            font-size: 15pt;
            line-height: 1.0;
            margin-bottom: 0.02in;
          }

          .first-name {
            font-size: 10.5pt;
            line-height: 1.0;
          }

          /* Address Section */
          .address-section {
            flex: 1;
            margin-bottom: 0.04in;
          }

          .address-line {
            font-size: 10pt;
            line-height: 1.1;
            margin-bottom: 0.015in;
          }

          /* GP-887: second address line — delivery instructions / unit notes */
          .instructions-line {
            font-size: 8.5pt;
            line-height: 1.1;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .order-info-row {
            display: flex;
            flex-wrap: wrap; /* PRX-39: a crowded row wraps instead of clipping the date */
            align-items: center;
            gap: 0.06in;
            margin-top: auto;
          }

          .order-number {
            font-size: 6pt;
            font-weight: bold;
          }

          /* GP-1234: fulfillment type — the dominant chip on the info row so a
             pickup is readable at a glance on the shelf. Deliberately a compact
             high-contrast band, not an extra paragraph: the label is only
             1-1/8" tall and every row competes for the same space. */
          .fulfillment-badge {
            font-size: 11pt;
            font-weight: bold;
            letter-spacing: 0.5px;
            background: #000;
            color: #fff;
            padding: 0 4px;
            white-space: nowrap;
          }

          .delivery-type {
            font-size: 8pt;
            font-weight: bold;
            background: #000;
            color: #fff;
            padding: 0 2px;
            white-space: nowrap;
          }

          /* GP-521/GP-664: nursing-home group name — the key staff sort packaged
             orders by after Product Verification, so it leads the badge row. */
          .nh-group {
            font-size: 6pt;
            font-weight: bold;
            background: #000;
            color: #fff;
            padding: 0 2px;
            white-space: nowrap;
            max-width: 100%;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .sig-required {
            font-size: 8pt;
            font-weight: bold;
            background: #d32f2f;
            color: #fff;
            padding: 1px 4px;
            border: 1px solid #b71c1c;
            white-space: nowrap;
          }

          .shipment-date {
            font-size: 8.5pt;
            white-space: nowrap;
            flex-shrink: 0; /* PRX-39: never truncate the date to make room for a badge */
          }

          .multi-bag {
            font-size: 8.5pt;
            font-weight: bold;
            background: #000;
            color: #fff;
            padding: 1px 4px;
            display: inline-block;
            margin-top: 1px;
          }

          /* Right Section: Barcode */
          /* OLD: QR Code styles - commented out for future reversion
          .qr-section {
            display: flex;
            align-items: center;
            justify-content: center;
            border-left: 1px solid #ccc;
            padding-left: 0.08in;
            width: 0.9in;
          }

          #delivery-qr-code {
            width: 80px !important;
            height: 80px !important;
          }

          #delivery-qr-code img {
            width: 80px !important;
            height: 80px !important;
            display: block;
          }
          */

          .barcode-section {
            display: flex;
            align-items: center;
            justify-content: center;
            border-left: 1px solid #ccc;
            padding-left: 0.08in;
            /* GP-887: narrowed so the text column gets more room */
            width: 1.25in;
          }

          #delivery-barcode {
            max-width: 100%;
            height: auto;
          }
          ${multi ? MULTI_LABEL_CSS : ''}
        </style>
        <!-- OLD: QR Code library - commented out for future reversion
        <script src="https://cdnjs.cloudflare.com/ajax/libs/qrcodejs/1.0.0/qrcode.min.js"></script>
        -->
      </head>
      <body>
        ${labelsHtml}
        <script>
          /* OLD: QR Code generation - commented out for future reversion
          function initQRCode() {
            try {
              const qrContainer = document.getElementById('delivery-qr-code');

              if (typeof QRCode !== 'undefined') {
                // Clear the img tag and replace with div
                qrContainer.outerHTML = '<div id="delivery-qr-code"></div>';
                const qrDiv = document.getElementById('delivery-qr-code');

                // Generate QR code
                new QRCode(qrDiv, {
                  text: '<order number>',
                  width: 80,
                  height: 80,
                  colorDark: '#000000',
                  colorLight: '#ffffff',
                  correctLevel: QRCode.CorrectLevel.M
                });

                // Print after QR code is generated
                setTimeout(function() {
                  window.print();
                  setTimeout(function() { window.close(); }, 500);
                }, 500);
              } else {
                console.error('QRCode library not loaded');
                // Print without QR code
                setTimeout(function() {
                  window.print();
                  setTimeout(function() { window.close(); }, 500);
                }, 250);
              }
            } catch(e) {
              console.error('QR Code generation failed:', e);
              // Print without QR code
              setTimeout(function() {
                window.print();
                setTimeout(function() { window.close(); }, 500);
              }, 250);
            }
          }

          // Try to load QR code when ready
          if (document.readyState === 'complete') {
            initQRCode();
          } else {
            window.onload = initQRCode;
          }
          */

          // PRX-39: the barcode is already inline SVG in the markup above —
          // nothing to load, nothing to race. Print once the document is ready.
          function printLabel() {
            setTimeout(function() {
              window.print();
              setTimeout(function() { window.close(); }, 500);
            }, 250);
          }
          if (document.readyState === 'complete') {
            printLabel();
          } else {
            window.onload = printLabel;
          }
        </script>
      </body>
    </html>
  `);

  printWindow.document.close();
  return true;
};

const singleLabelTitle = (data: DeliveryLabelData): string =>
  `${resolveFulfillmentType(data.deliveryMethod) === 'PICKUP' ? 'Pickup' : 'Delivery'} Label - ${escapeHtml(data.orderNumber)}`;

export const printDeliveryLabel = (data: DeliveryLabelData) => {
  writeDeliveryLabelWindow(buildDeliveryLabelMarkup(data), singleLabelTitle(data), false);
};

/**
 * PRX-165 WP-26 — every label in one window. One label prints exactly as
 * `printDeliveryLabel`. `target` is a window the caller opened during the
 * click (a popup opened after an await is blocked); null means it was blocked
 * then. Answers whether the labels were written.
 */
export const printDeliveryLabels = (
  labels: readonly DeliveryLabelData[],
  target?: Window | null,
): boolean => {
  if (labels.length === 0) return true;
  if (labels.length === 1) {
    return writeDeliveryLabelWindow(buildDeliveryLabelMarkup(labels[0]), singleLabelTitle(labels[0]), false, target);
  }
  return writeDeliveryLabelWindow(
    labels.map(buildDeliveryLabelMarkup).join(''),
    `Delivery labels - ${labels.length}`,
    true,
    target,
  );
};
